import React, { FC, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Auth from '@aws-amplify/auth';
import { Formik } from 'formik';

import { ClientInvitationStatus } from 'api/public';
import { Layouts } from 'components';
import { login, signUp } from 'paths';
import { useNotify, useGlobalClient } from 'hooks';
import { getAppImage } from 'helpers';

import { SignUpSchema, ISignUpValues } from '../validations';
import { EnsureInvitedUserConfirmed, SignUpForm } from '../components';
import { TOKEN_PARAM, TOKEN_ATTRIBUTE, CLIENT_ATTRIBUTE } from '../constants';
import { AuthContainerProps } from '../types';

const Invitation: FC<AuthContainerProps> = ({ updateUsername, updatePassword }) => {
  const notify = useNotify();
  const client = useGlobalClient();
  const url = new URL(window.location.href);

  const [working, setWorking] = useState(false);
  const [token] = useState(url.searchParams.get(TOKEN_PARAM));

  async function signIn(username: string, password: string) {
    try {
      await Auth.signIn(username, password);
    } catch (error) {
      notify.error({ message: error.message });
    }
  }

  async function onSubmit({ username, password }: ISignUpValues) {
    setWorking(true);

    const options = {
      username: username.toLowerCase(),
      password,
      attributes: {
        email: username,
        [TOKEN_ATTRIBUTE]: token,
        [CLIENT_ATTRIBUTE]: client.id,
      },
    };

    try {
      const { userConfirmed } = await Auth.signUp(options);
      if (userConfirmed) {
        void signIn(options.username, options.password);
      }
    } catch (error) {
      if (error?.message) {
        error.message = error.message.split('PreSignUp failed with error ').join('');
      }
      setWorking(false);
      notify.error({ message: error.message });
    }
  }

  function renderImage() {
    return <img width={500} height={500} {...getAppImage('illustrations/boat.png', 500)} />;
  }

  function renderMain() {
    return (
      <EnsureInvitedUserConfirmed>
        {invitation => {
          if (!invitation) {
            return <Navigate to={signUp} replace />;
          }
          const isAccepted = invitation.invitation_status === ClientInvitationStatus.Accepted;
          if (isAccepted) {
            updateUsername(invitation.email_address);
            return <Navigate to={login} replace />;
          }

          const initialValues: ISignUpValues = {
            username: invitation.email_address,
            password: '',
          };

          return (
            <Formik
              onSubmit={onSubmit}
              initialValues={initialValues}
              validationSchema={SignUpSchema}
            >
              <SignUpForm
                hiddenEmail
                updateUsername={updateUsername}
                updatePassword={updatePassword}
                working={working}
              />
            </Formik>
          );
        }}
      </EnsureInvitedUserConfirmed>
    );
  }

  return <Layouts.Onboarding main={renderMain()} image={renderImage()} />;
};

export default Invitation;
